import type { ApolloClient } from '@apollo/client';
import { from, map } from 'rxjs';
import { SwarmType } from '@/graphql/types';
import { vperPeriodsQuery } from '../../../../../../graph/queries';
import { getDataType } from '../../../../../../graph/data-types';
import { format } from '../../../../../../utils/formatting';
import { buildRequiredMeasuringPointWindowInput } from '../measuringPointInput';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import {
    RuntimeDataService,
    type RuntimeDataServiceRequest,
    type RuntimeResultLinkDescriptorTabular,
    type RuntimeTabularColumnDescriptor,
} from '../types';
import {
    isRecord,
    normalizeArrayEntries,
    normalizeNullableBoolean,
    normalizeNullableFiniteNumber,
    normalizeNullableInteger,
    normalizeNullableRecord,
    normalizeOrderedTimeWindow,
    normalizeRequiredFiniteNumber,
    normalizeRequiredPositiveIntegerId,
    normalizeRequiredRecord,
    toPositiveIntegerIdNumber,
} from '../validationPrimitives';

const VPER_PERIOD_NAMES = ['DAY', 'EVENING', 'NIGHT'] as const;
const VPER_AXES = ['x', 'y', 'z'] as const;
const VPER_VECTOR_KEYS = ['vmax', 'vper'] as const;
const VPER_LIMIT_NAMES = ['A1', 'A2', 'A3'] as const;

type VperPeriodName = (typeof VPER_PERIOD_NAMES)[number] | null;
type VperAxisName = (typeof VPER_AXES)[number];
type VperVectorColumnKey = (typeof VPER_VECTOR_KEYS)[number];
type VperLimitName = (typeof VPER_LIMIT_NAMES)[number];
type VperPeriodEntry = RuntimeDataServiceOutput<'measuring_point.vper_periods'>['periods'][number];
type VperAxisValues = VperPeriodEntry['vmax'];

interface VperPeriodsQueryVariables {
    measuringPointId: number;
    startTime: number;
    endTime: number;
}

interface VperPeriodsQueryData {
    rangedSamples?: unknown[] | null;
}

const VPER_PERIOD_TRANSLATIONS: Record<Exclude<VperPeriodName, null>, string> = {
    DAY: gettext('Day'),
    EVENING: gettext('Evening'),
    NIGHT: gettext('Night'),
};

function isOneOf<TValue extends string>(
    values: readonly TValue[],
    value: unknown
): value is TValue {
    return typeof value === 'string' && values.includes(value as TValue);
}

function toMeasuringPointIdNumber(measuringPointId: string): number {
    return toPositiveIntegerIdNumber(
        measuringPointId,
        'measuring_point.vper_periods.measuringPointId'
    );
}

function normalizeVperPeriodName(value: unknown, errorLabel: string): VperPeriodName {
    if (value == null) {
        return null;
    }

    if (isOneOf(VPER_PERIOD_NAMES, value)) {
        return value;
    }

    throw new Error(`Expected "${errorLabel}" to be a VPER period name or null.`);
}

function normalizeVperAxisValues(value: unknown, pathLabel: string): VperAxisValues {
    const axisValues = normalizeNullableRecord(value, pathLabel);

    return {
        x: normalizeNullableFiniteNumber(axisValues?.x, `${pathLabel}.x`),
        y: normalizeNullableFiniteNumber(axisValues?.y, `${pathLabel}.y`),
        z: normalizeNullableFiniteNumber(axisValues?.z, `${pathLabel}.z`),
    };
}

function normalizeVperTimeSpan(value: unknown, pathLabel: string): VperPeriodEntry['timespan'] {
    const timeSpan = normalizeRequiredRecord(value, pathLabel);

    return {
        from: normalizeRequiredFiniteNumber(timeSpan.from, `${pathLabel}.from`),
        to: normalizeRequiredFiniteNumber(timeSpan.to, `${pathLabel}.to`),
    };
}

function normalizeVperPeriodCompleteness(
    value: unknown,
    pathLabel: string
): VperPeriodEntry['periodCompleteness'] {
    const completeness = normalizeNullableRecord(value, pathLabel);
    if (!completeness) {
        return null;
    }

    return {
        completeness: normalizeNullableInteger(
            completeness.completeness,
            `${pathLabel}.completeness`
        ),
    };
}

function normalizeVperLimits(value: unknown, pathLabel: string): VperPeriodEntry['limits'] {
    const limits = normalizeNullableRecord(value, pathLabel);

    return {
        A1: normalizeNullableFiniteNumber(limits?.A1, `${pathLabel}.A1`),
        A2: normalizeNullableFiniteNumber(limits?.A2, `${pathLabel}.A2`),
        A3: normalizeNullableFiniteNumber(limits?.A3, `${pathLabel}.A3`),
        complies: normalizeNullableBoolean(limits?.complies, `${pathLabel}.complies`),
    };
}

function normalizeVperPeriodEntry(value: unknown, pathLabel: string): VperPeriodEntry {
    const entry = normalizeRequiredRecord(value, pathLabel);

    return {
        timestamp: normalizeRequiredFiniteNumber(entry.timestamp, `${pathLabel}.timestamp`),
        timespan: normalizeVperTimeSpan(entry.timespan, `${pathLabel}.timespan`),
        period: normalizeVperPeriodName(entry.period, `${pathLabel}.period`),
        periodCompleteness: normalizeVperPeriodCompleteness(
            entry.periodCompleteness,
            `${pathLabel}.periodCompleteness`
        ),
        vmax: normalizeVperAxisValues(entry.vmax, `${pathLabel}.vmax`),
        vper: normalizeVperAxisValues(entry.vper, `${pathLabel}.vper`),
        limits: normalizeVperLimits(entry.limits, `${pathLabel}.limits`),
    };
}

function validateVperPeriodsInput(
    input: unknown
): RuntimeDataServiceInput<'measuring_point.vper_periods'> {
    if (!isRecord(input)) {
        throw new Error('Expected "measuring_point.vper_periods" input to be an object.');
    }

    const { startTime, endTime } = normalizeOrderedTimeWindow(
        input.startTime,
        input.endTime,
        'measuring_point.vper_periods'
    );

    return {
        measuringPointId: normalizeRequiredPositiveIntegerId(
            input.measuringPointId,
            'measuring_point.vper_periods.measuringPointId'
        ),
        startTime,
        endTime,
    };
}

function validateVperPeriodsResult(
    result: unknown
): RuntimeDataServiceOutput<'measuring_point.vper_periods'> {
    if (!isRecord(result)) {
        throw new Error('Expected "measuring_point.vper_periods" result to be an object.');
    }

    return {
        periods: normalizeArrayEntries(
            result.periods,
            'measuring_point.vper_periods.periods',
            normalizeVperPeriodEntry
        ),
    };
}

function parseVperPeriodsTableRows(value: unknown): readonly VperPeriodEntry[] {
    return validateVperPeriodsResult({ periods: value }).periods;
}

function formatPeriod(entry: VperPeriodEntry, timezone: string): string {
    const periodStart = format(new Date(entry.timespan.from), { timezone, hideDate: true });
    const periodEnd = format(new Date(entry.timespan.to), { timezone, hideDate: true });
    const periodName = entry.period ? VPER_PERIOD_TRANSLATIONS[entry.period] : null;
    const periodLabel = periodName
        ? `${periodName} (${periodStart}-${periodEnd})`
        : `${periodStart}-${periodEnd}`;
    const completeness = entry.periodCompleteness?.completeness;

    return completeness == null ? periodLabel : `${periodLabel} (${completeness}%)`;
}

function createVectorColumn(
    vectorKey: VperVectorColumnKey,
    axis: VperAxisName
): RuntimeTabularColumnDescriptor<VperPeriodEntry> {
    return {
        kind: 'measurement',
        key: `${vectorKey}_${axis}`,
        dataType: vectorKey,
        qualifier: axis,
        defaultAlignment: 'right',
        resolveCell: (entry: VperPeriodEntry) => entry[vectorKey][axis],
    };
}

function createLimitColumn(limit: VperLimitName): RuntimeTabularColumnDescriptor<VperPeriodEntry> {
    const limitIndex = VPER_LIMIT_NAMES.indexOf(limit);
    const valueName = getDataType('vper').valueNames?.[limitIndex];
    if (!valueName) {
        throw new Error(`Missing value name for VPER limit "${limit}".`);
    }

    // The limits are velocity thresholds, so they share `vmax`'s unit and rounding; only their
    // name differs, which the label override supplies as a naming-convention token.
    return {
        kind: 'measurement',
        key: limit.toLowerCase(),
        dataType: 'vmax',
        label: valueName,
        defaultAlignment: 'right',
        resolveCell: (entry: VperPeriodEntry) => entry.limits[limit],
    };
}

function createVperPeriodColumns() {
    return [
        {
            kind: 'text',
            key: 'date',
            requiresTimezone: true,
            label: gettext('Date'),
            defaultAlignment: 'left',
            resolveCell: (entry: VperPeriodEntry, options) =>
                format(new Date(entry.timestamp), {
                    timezone: options.timezone,
                    hideTime: true,
                }),
        },
        {
            kind: 'text',
            key: 'period',
            requiresTimezone: true,
            label: gettext('Period'),
            defaultAlignment: 'left',
            resolveCell: (entry: VperPeriodEntry, options) => formatPeriod(entry, options.timezone),
        },
        ...VPER_VECTOR_KEYS.flatMap((vectorKey) =>
            VPER_AXES.map((axis) => createVectorColumn(vectorKey, axis))
        ),
        ...VPER_LIMIT_NAMES.map((limit) => createLimitColumn(limit)),
        {
            kind: 'text',
            key: 'remarks',
            label: gettext('Remarks'),
            defaultAlignment: 'left',
            resolveCell: (entry: VperPeriodEntry) => {
                if (entry.limits.complies === null) {
                    return '';
                }

                return entry.limits.complies ? gettext('Complies') : gettext('Noncomplying');
            },
        },
    ] as const satisfies readonly RuntimeTabularColumnDescriptor<VperPeriodEntry>[];
}

export class VperPeriodsRuntimeDataService extends RuntimeDataService<'measuring_point.vper_periods'> {
    static readonly serviceId = 'measuring_point.vper_periods' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_VPER_PERIODS_LABEL'),
            description: gettext('DATA_SERVICE_VPER_PERIODS_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        return [
            {
                key: 'periods',
                label: gettext('DATA_SERVICE_VPER_PERIODS_PERIODS_LABEL'),
                description: gettext('DATA_SERVICE_VPER_PERIODS_PERIODS_DESCRIPTION'),
                supportedContextKinds: ['measuring-point'],
                supportedSwarmTypes: [SwarmType.Vibration],
                path: 'periods',
                valueKind: 'tabular',
                argPolicy: 'forbidden',
                exposeInInsertionPresets: true,
                columns: createVperPeriodColumns(),
                parseTableRows: parseVperPeriodsTableRows,
                buildInput: (args) => buildRequiredMeasuringPointWindowInput(args, 'VPER periods'),
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptorTabular<
            'measuring_point.vper_periods',
            VperPeriodEntry
        >[];
    }

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    validateArgs = validateVperPeriodsInput;

    validateResult = validateVperPeriodsResult;

    createResult$ = ({ input }: RuntimeDataServiceRequest<'measuring_point.vper_periods'>) =>
        from(
            this.client.query<VperPeriodsQueryData, VperPeriodsQueryVariables>({
                query: vperPeriodsQuery,
                variables: {
                    measuringPointId: toMeasuringPointIdNumber(input.measuringPointId),
                    startTime: input.startTime,
                    endTime: input.endTime,
                },
                fetchPolicy: 'cache-first',
            })
        ).pipe(
            map(({ data }) => {
                if (!data) {
                    throw new Error('Missing VPER periods query result for runtime data services.');
                }

                // Backend resolves rangedSamples to null (not []) for a legitimate empty state
                // (no sensor ever attached), so it's treated as nullish-allowed here, not an error.
                return validateVperPeriodsResult({ periods: data.rangedSamples ?? [] });
            })
        );
}

export function createVperPeriodsService(
    client: ApolloClient<object>
): VperPeriodsRuntimeDataService {
    return new VperPeriodsRuntimeDataService(client);
}
